import { fetchJson } from '../../http/utils.js';
import { logger } from './logger.js';

/**
 * Utility helpers for the ticket page modules.
 */

function safeJSONParse(value, fallback = null) {
  if (value == null || value === '') {
    return fallback;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    logger.warn('Failed to parse JSON attribute', error);
    return fallback;
  }
}

export function getHostConfig(host) {
  const dataset = host?.dataset || {};
  return {
    eventName: dataset.eventName || null,
    eventSlug: dataset.eventSlug || null,
    accountData: safeJSONParse(dataset.account, null),
    showRemaining: dataset.showRemaining === '1' || dataset.showRemaining === 'true',
  };
}

export function getParamOrHidden(form, name) {
  const keys = Array.isArray(name) ? name : [name];
  const params = new URLSearchParams(window.location.search);
  for (const key of keys) {
    const fromUrl = params.get(key);
    if (fromUrl != null && fromUrl !== '') {
      return fromUrl;
    }
  }
  for (const key of keys) {
    const input = form?.querySelector(`input[name="${key}"]`);
    if (input && input.value) {
      return input.value;
    }
  }
  return '';
}

export function collectCustomFields(form) {
  if (!form) {
    return null;
  }
  const fields = Array.from(form.querySelectorAll('[data-custom-field]'));
  if (!fields.length) {
    return null;
  }
  const payload = {};
  fields.forEach((el) => {
    const key = el.dataset.customField || el.getAttribute('data-custom-field');
    if (!key) {
      return;
    }
    if (el.type === 'checkbox') {
      payload[key] = !!el.checked;
      return;
    }
    const value = (el.value || '').trim();
    if (value !== '') {
      payload[key] = value;
    }
  });
  return Object.keys(payload).length ? payload : null;
}

export function createFocusTrap(modal, { onEscape, submitInput, onSubmit, initialFocus } = {}) {
  const FOCUSABLE = [
    'a[href]',
    'button:not(:disabled)',
    'input:not(:disabled)',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  function isVisible(el) {
    if (el.closest('[hidden]')) return false;
    const style = getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden'
      && (el.offsetParent !== null || style.position === 'fixed');
  }

  function getFocusable() {
    if (!modal) return [];
    return Array.from(modal.querySelectorAll(FOCUSABLE)).filter(isVisible);
  }

  function handleKeydown(event) {
    if (!modal || modal.classList.contains('hidden') || modal.classList.contains('checkout-hidden')) {
      return;
    }
    if (event.key === 'Escape') {
      if (typeof onEscape === 'function') {
        onEscape(event);
      }
      return;
    }
    if (
      event.key === 'Enter' &&
      submitInput &&
      document.activeElement === submitInput &&
      typeof onSubmit === 'function'
    ) {
      event.preventDefault();
      onSubmit(event);
      return;
    }
    if (event.key === 'Tab') {
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first || !modal.contains(document.activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !modal.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }

  if (initialFocus) {
    requestAnimationFrame(() => {
      const target = typeof initialFocus === 'string' ? modal?.querySelector(initialFocus) : initialFocus;
      if (target && typeof target.focus === 'function') target.focus();
    });
  }

  return handleKeydown;
}

export function createSoftReload({ selector, onReplace, onError } = {}) {
  let busy = false;
  return async function softReload() {
    if (busy) {
      return;
    }
    const host = document.querySelector(selector || 'section#tickets');
    if (!host) {
      return;
    }
    busy = true;
    host.style.transition = host.style.transition || 'opacity .18s ease';
    host.style.opacity = '0.0';

    const url = new URL(window.location.href);
    url.hash = '';

    let html;
    try {
      const { res } = await fetchJson(url.toString(), {
        method: 'GET',
        cache: 'no-store',
        headers: { Accept: 'text/html, */*;q=0.9' },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      html = await res.text();
    } catch (error) {
      busy = false;
      if (typeof onError === 'function') {
        onError(error);
      } else {
        setTimeout(() => window.location.reload(), 150);
      }
      return;
    }

    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector(selector || 'section#tickets');
    if (!fresh) {
      busy = false;
      if (typeof onError === 'function') {
        onError(new Error('Missing fresh ticket section'));
      } else {
        setTimeout(() => window.location.reload(), 150);
      }
      return;
    }

    host.replaceWith(fresh);
    requestAnimationFrame(() => {
      fresh.style.transition = 'opacity .18s ease';
      fresh.style.opacity = '0.0';
      requestAnimationFrame(() => {
        fresh.style.opacity = '1';
      });
    });

    busy = false;
    if (typeof onReplace === 'function') {
      onReplace(fresh);
    }
  };
}
